// hooks
import { useState, useCallback } from 'react';
import useAuth, { useAuthActions } from '@/features/auth/hooks/useAuth';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import useUsers from '@/features/users/hooks/useUsers';
import { useDispatch } from 'react-redux';
import useBranding from '@/hooks/useBranding';

// Mui components
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';

// components
import { Link } from 'react-router-dom';
import NavbarDrawer from './NavbarDrawer';
import HideOnScroll from '@/components/shared/HideOnScroll';
import { InnerColumn } from '@/components/shared/PageComponents';
import Tooltip from '@/components/shared/Tooltip';
import NavbarCheckboxes from './NavbarCheckboxes';
import NavbarDropdowns from './NavbarDropdowns';
import UserSearchBar from './UserSearchBar';
import NotificationsButton from './NotificationsButton';
import MessengerButton from '@/features/messenger/components/MessengerButton';

// icons
import KeyIcon from '@mui/icons-material/VpnKey';
import MenuIcon from '@mui/icons-material/Menu'; // burger icon
import SchoolIcon from '@mui/icons-material/School';

const useStyles = makeStyles((theme) => ({
  offset: {
    ...theme.mixins.toolbar,
    minHeight: '48px',
  },
  toolbarDistance: {
    minHeight: '20px',
  },

  toolbar: {
    minHeight: '48px !important',
  },
}));

export default function Navbar({
  showDropdowns,
  showLess = true,
  showCheckboxes,
  noLogin = false,
  noSpacer = false,
  noGuide = false,
}) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMessengerDropdownOpen, setIsMessengerDropdownOpen] = useState(false);

  const classes = useStyles();

  const { usersLoaded, usersSearchValue } = useUsers();
  const { currentUser } = useAuth();
  const { handleLogin } = useAuthActions();
  const dispatch = useDispatch();
  const { brandName, logoUrl, navbarLogoSize, navbarPadding, showNavbarTitle } = useBranding();

  const openMessengerDropdown = useCallback(() => {
    setIsMessengerDropdownOpen((prevState) => !prevState);
  }, []);

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="sticky"
          sx={(theme) => ({
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          })}>
          <Toolbar
            className={classes.toolbar}
            sx={{ minHeight: '48px !important', py: `${navbarPadding}px` }}>
            <InnerColumn>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 2,
                }}>
                {/* Left Section - Logo & Search */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                  }}>
                  <Link
                    to="/"
                    className="link-2"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = 'scale(1.05)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = 'scale(1)')
                    }>
                    <img
                      src={logoUrl}
                      style={{
                        width: `${navbarLogoSize}px`,
                        height: 'auto',
                      }}
                      alt={brandName}
                    />
                    {showNavbarTitle && (
                      <Hidden mdDown>
                        <Typography
                          component="h1"
                          sx={{
                            fontSize: '1.3rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}>
                          {brandName}
                        </Typography>
                      </Hidden>
                    )}
                  </Link>

                  {/* Search Bar */}
                  {usersLoaded && currentUser?.uid && (
                    <ClickAwayListener
                      onClickAway={() => {
                        if (isSearchOpen) setIsSearchOpen(false);
                        if (usersSearchValue) {
                          dispatch({ type: 'users/setSearch', payload: '' });
                        }
                      }}>
                      <Box
                        onClick={() => setIsSearchOpen(true)}
                        sx={{ minWidth: 120, maxWidth: 240 }}>
                        <UserSearchBar
                          setIsSearchOpen={setIsSearchOpen}
                          isSearchOpen={isSearchOpen}
                        />
                      </Box>
                    </ClickAwayListener>
                  )}
                </Box>

                {/* Center Section - Controls (when expanded) */}
                {!showLess && (
                  <Hidden lgDown>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}>
                      {showCheckboxes && <NavbarCheckboxes compact />}
                      {showDropdowns && <NavbarDropdowns compact />}
                    </Box>
                  </Hidden>
                )}

                {/* Right Section - User Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!currentUser?.uid && !noGuide && (
                    <Button
                      component={Link}
                      to="/guide"
                      variant="outlined"
                      size="small"
                      startIcon={<SchoolIcon />}
                      sx={(theme) => ({
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      })}>
                      <Hidden smDown>Guide</Hidden>
                    </Button>
                  )}

                  {!currentUser?.uid && !noLogin && (
                    <Button
                      onClick={handleLogin}
                      variant="contained"
                      size="small"
                      startIcon={<KeyIcon />}
                      sx={(theme) => ({
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        },
                      })}>
                      Log In
                    </Button>
                  )}

                  {currentUser?.uid && (
                    <>
                      <MessengerButton
                        isMessengerDropdownOpen={isMessengerDropdownOpen}
                        setIsMessengerDropdownOpen={setIsMessengerDropdownOpen}
                        isScrim={false}
                        onClick={openMessengerDropdown}
                      />
                      <NotificationsButton />
                      <Tooltip title="More options">
                        <IconButton
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setIsDrawerOpen(true)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}>
                          <MenuIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            </InnerColumn>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <NavbarDrawer
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        showCheckboxes={showCheckboxes}
        showDropdowns={showDropdowns}
        showLess={showLess}
      />

      <div className={classes.offset} />
    </>
  );
}
